import React, { useState, useEffect } from 'react';
import { TableContainer, Paper, Table, TableHead, TableRow, TableCell, Box, TableBody, TablePagination, Chip, IconButton, Skeleton, Typography, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReportService from '../../../api/services/reportService';
import EditScheduleReportModal from './EditScheduleReportModal';
import { extractTotalCount } from '../../../components/common/tableStyles';

export default function ScheduledReportsTab({ tenantId, refreshTrigger }) {
    const [scheduledData, setScheduledData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [scheduledPage, setScheduledPage] = useState(0);
    const [scheduledRowsPerPage, setScheduledRowsPerPage] = useState(20);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [scheduledTotal, setScheduledTotal] = useState(0);
    
    // Delete Modal State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    useEffect(() => {
        fetchScheduledReports();
    }, [tenantId, refreshTrigger, scheduledPage, scheduledRowsPerPage]);

    const fetchScheduledReports = async () => {
        setIsLoading(true);
        try {
            const res = await ReportService.getScheduledReports(tenantId, {
                page: scheduledPage,
                limit: scheduledRowsPerPage
            });
            const data = Array.isArray(res?.data) ? res.data : (res?.data?.scheduledReports || res?.data?.data || []);
            setScheduledData(data);
            setScheduledTotal(extractTotalCount(res, data));
        } catch (error) {
            console.error('Failed to load scheduled reports:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleScheduledChangePage = (event, newPage) => setScheduledPage(newPage);
    const handleScheduledChangeRowsPerPage = (event) => {
        setScheduledRowsPerPage(parseInt(event.target.value, 10));
        setScheduledPage(0);
    };

    const handleEditClick = (record) => {
        setSelectedRecord(record);
        setEditModalOpen(true);
    };

    const handleEditSuccess = (msg) => {
        setSuccessMessage(msg);
        fetchScheduledReports();
    };

    const handleDeleteClick = (record) => {
        setRecordToDelete(record);
        setDeleteModalOpen(true);
        setDeleteError('');
    };

    const handleDeleteConfirm = async () => {
        if (!recordToDelete) return;
        setIsDeleting(true);
        setDeleteError('');
        try {
            await ReportService.deleteScheduledReport(tenantId, recordToDelete.id);
            setSuccessMessage('Automated report schedule permanently removed');
            setDeleteModalOpen(false);
            fetchScheduledReports();
        } catch (error) {
            console.error('Failed to delete scheduled report:', error);
            setDeleteError(error.response?.data?.message || 'Failed to delete schedule. Please check server connection.');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Box>
            <Snackbar 
                open={!!successMessage} 
                autoHideDuration={6000} 
                onClose={() => setSuccessMessage('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setSuccessMessage('')}>{successMessage}</Alert>
            </Snackbar>

            <EditScheduleReportModal
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                tenantId={tenantId}
                editData={selectedRecord}
                onSuccess={handleEditSuccess}
            />

            <Dialog open={deleteModalOpen} onClose={isDeleting ? undefined : () => setDeleteModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>Delete Scheduled Report</DialogTitle>
                <DialogContent dividers>
                    {deleteError && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'error.main', color: 'white', borderRadius: 1 }}>
                            <Typography variant="body2">{deleteError}</Typography>
                        </Box>
                    )}
                    <Typography>
                        Are you sure you want to delete this scheduled report? This action is permanent and will stop all future automatic email distributions for this specific configuration.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDeleteModalOpen(false)} disabled={isDeleting} color="inherit">
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        color="error" 
                        onClick={handleDeleteConfirm} 
                        disabled={isDeleting}
                        startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                    >
                        {isDeleting ? 'Deleting...' : 'Confirm Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Report Name</TableCell>
                            <TableCell>Frequency</TableCell>
                            <TableCell>Recipients</TableCell>
                            <TableCell>Next Run</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                <TableBody>
                    {isLoading ? (
                        Array.from(new Array(5)).map((_, idx) => (
                            <TableRow key={idx}>
                                <TableCell><Skeleton animation="wave" /></TableCell>
                                <TableCell><Skeleton animation="wave" /></TableCell>
                                <TableCell><Skeleton animation="wave" /></TableCell>
                                <TableCell><Skeleton animation="wave" /></TableCell>
                                <TableCell><Skeleton animation="wave" /></TableCell>
                                <TableCell><Skeleton animation="wave" /></TableCell>
                            </TableRow>
                        ))
                    ) : (
                        scheduledData.length > 0 ? (
                            scheduledData.map((s, idx) => (
                                <TableRow key={s.id || idx} hover>
                                    <TableCell><b>{s.name || s.reportType || '-'}</b></TableCell>
                                    <TableCell>{s.frequency || '-'}</TableCell>
                                    <TableCell>{Array.isArray(s.recipients) ? s.recipients.join(', ') : (s.recipients || '-')}</TableCell>
                                    <TableCell>{s.nextRun || s.deliveryTime || '-'}</TableCell>
                                    <TableCell><Chip label={s.status || 'Active'} size="small" color={s.status === 'Failed' ? 'error' : (s.status === 'Paused' || s.status === 'Inactive' ? 'warning' : 'success')} /></TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" color="primary" onClick={() => handleEditClick(s)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(s)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                    <Typography color="text.secondary">No scheduled reports found.</Typography>
                                </TableCell>
                            </TableRow>
                        )
                    )}
                </TableBody>
            </Table>
            <TablePagination
                component="div"
                count={scheduledData.length === 0 ? 0 : scheduledTotal}
                page={scheduledPage}
                onPageChange={handleScheduledChangePage}
                rowsPerPage={scheduledRowsPerPage}
                onRowsPerPageChange={handleScheduledChangeRowsPerPage}
                rowsPerPageOptions={[20, 50, 100]}
            />
            </TableContainer>
        </Box>
    );
}
